import React, { useEffect, useState } from "react";
import Layout from "../Layout";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import moment from "moment";
import { BiChevronLeft, BiChevronRight, BiPlus, BiTime } from "react-icons/bi";
import { HiOutlineViewGrid } from "react-icons/hi";
import { HiOutlineCalendarDays } from "react-icons/hi2";
import AddAppointmentModal from "../components/Modals/AddApointmentModal";
import {
  getAllCitas,
  getCitas,
  getCitasError,
  getCitasStatus,
  updateCita,
} from "../redux/features/citasSlice";
import { useDispatch, useSelector } from "react-redux";
import { getAllPatients, getPatients } from "../redux/features/patientSlice";
import "moment/locale/es.js";
import ConfirmationAlert from "../components/ConfirmationAlert";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// custom toolbar
const CustomToolbar = (
  toolbar,
  events,
  setSelectedSlot,
  calculateScrollTime
) => {
  // today button handler
  const goToBack = () => {
    if (toolbar.view === "month")
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
    if (toolbar.view === "day")
      toolbar.date.setDate(toolbar.date.getDate() - 1);
    if (toolbar.view === "week")
      toolbar.date.setDate(toolbar.date.getDate() - 7);
    toolbar.onNavigate("prev");

    if (toolbar.view === "day" && calculateScrollTime) {
      calculateScrollTime(toolbar.date);
    }
  };

  // next button handler
  const goToNext = () => {
    if (toolbar.view === "month")
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
    if (toolbar.view === "day")
      toolbar.date.setDate(toolbar.date.getDate() + 1);
    if (toolbar.view === "week")
      toolbar.date.setDate(toolbar.date.getDate() + 7);
    toolbar.onNavigate("next");

    if (toolbar.view === "day" && calculateScrollTime) {
      calculateScrollTime(toolbar.date);
    }
  };

  // today button handler
  const goToCurrent = () => {
    toolbar.onNavigate("TODAY");

    if (toolbar.view === "day" && calculateScrollTime) {
      const today = new Date();
      calculateScrollTime(today);
    }
  };

  // month button handler
  const goToMonth = () => {
    toolbar.onView("month");
  };

  // week button handler
  const goToWeek = () => {
    toolbar.onView("week");
  };

  // day button handler
  const goToDay = () => {
    toolbar.onView("day");

    if (calculateScrollTime) {
      calculateScrollTime(toolbar.date);
    }
  };

  // view button group
  const viewNamesGroup = [
    { view: "month", label: "Mes" },
    { view: "week", label: "Semana" },
    { view: "day", label: "Dia" },
  ];

  return (
    <div className="flex flex-col gap-8 mb-8">
      <h1 className="text-xl font-semibold">Agenda</h1>
      <div className="grid sm:grid-cols-2 md:grid-cols-12 gap-4">
        <div className="md:col-span-1 flex sm:justify-start justify-center items-center">
          <button
            onClick={goToCurrent}
            className="px-6 py-2 border border-subMain rounded-md text-subMain"
          >
            Hoy
          </button>
        </div>
        <div className="md:col-span-9 flex-rows gap-4">
          <button onClick={goToBack} className="text-2xl text-subMain">
            <BiChevronLeft />
          </button>
          <span className="text-xl font-semibold">
            {(toolbar.view === "day" &&
              moment(toolbar.date).format("D [de] MMMM [de] YYYY")) ||
              moment(toolbar.date)
                .format("MMMM YYYY")
                .replace(/^([a-z])/, (match) => match.toUpperCase())}
          </span>
          <button onClick={goToNext} className="text-2xl text-subMain">
            <BiChevronRight />
          </button>
        </div>

        <div className="md:col-span-2 grid grid-cols-3 rounded-md border border-subMain">
          {viewNamesGroup.map((item, index) => (
            <button
              key={index}
              title={item.label}
              onClick={
                item.view === "month"
                  ? goToMonth
                  : item.view === "week"
                  ? goToWeek
                  : goToDay
              }
              className={`border-l text-xl py-2 flex-colo border-subMain ${
                toolbar.view === item.view
                  ? "bg-subMain text-white"
                  : "text-subMain"
              }`}
            >
              {item.view === "month" ? (
                <HiOutlineViewGrid />
              ) : item.view === "week" ? (
                <HiOutlineCalendarDays />
              ) : (
                <BiTime />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

function Appointments() {
  const localizer = momentLocalizer(moment);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({});
  const [events, setEvents] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scrollToTime, setScrollToTime] = useState(() => {
    const defaultTime = new Date();
    defaultTime.setHours(7);
    defaultTime.setMinutes(0);
    defaultTime.setSeconds(0);
    return defaultTime;
  });

  const dispatch = useDispatch();
  const citas = useSelector(getAllCitas);
  const patients = useSelector(getAllPatients);

  const DndCalendar = withDragAndDrop(Calendar);

  const findEarliestEventOfDay = (events, date) => {
    const dateEvents = events.filter((event) => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear() &&
        event.estado.toLowerCase() === "en curso"
      );
    });

    if (dateEvents.length === 0) return null;
    dateEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
    return dateEvents[0];
  };

  const calculateScrollTime = (date) => {
    if (events.length === 0) return;

    const earliestEvent = findEarliestEventOfDay(events, date);

    if (earliestEvent) {
      const eventTime = new Date(earliestEvent.start);
      const scrollTime = new Date(eventTime);
      scrollTime.setMinutes(eventTime.getMinutes() - 30);

      const minTime = new Date(0, 0, 0, 7, 0, 0);
      if (scrollTime.getHours() < minTime.getHours()) {
        scrollTime.setHours(minTime.getHours());
        scrollTime.setMinutes(minTime.getMinutes());
      }

      setScrollToTime(scrollTime);
    }
  };

  // Convertir a formato ISO
  const convertToISO = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().replace(".000Z", ".000000Z");
  };

  // Manejar el arrastre de consultas
  const onEventDrop = ({ event, start, end }) => {
    if (event?.estado.toLowerCase() === "en curso") {
      ConfirmationAlert({
        title: "Edicion de cita",
        html: `
          <div style="text-align: center; font-size: 16px;">
            <p><strong>¿Está seguro de actualizar el horario de la cita?</strong></p>
            <p><strong>Nombre:</strong> ${event.patient.persona.nombre} ${
          event.patient.persona.apellidos
        }</p>
            <p><strong>Fecha:</strong> ${new Date(start).toLocaleDateString(
              "es-ES"
            )}</p>
            <p><strong>Hora:</strong> ${new Date(start).toLocaleTimeString(
              "es-ES"
            )} - ${new Date(end).toLocaleTimeString("es-ES")}</p>
          </div>
        `,
        onAccept: async () => {
          const currentEventsBackup = [...events];

          setEvents((currentEvents) =>
            currentEvents.map((evt) =>
              evt.id === event.id
                ? {
                    ...evt,
                    start: start,
                    end: end,
                    fecha: convertToISO(start),
                    hora_inicio: convertToISO(start),
                    hora_fin: convertToISO(end),
                  }
                : evt
            )
          );

          const visitaResponse = await dispatch(
            updateCita({
              ...event,
              fecha: convertToISO(start),
              hora_inicio: convertToISO(start),
              hora_fin: convertToISO(end),
            })
          );

          if (visitaResponse?.error) {
            setEvents(currentEventsBackup);
            toast.error(
              `No se pudo actualizar la cita: ${visitaResponse.error}`,
              { duration: 5000 }
            );
          } else {
            toast.success("Actualización de la cita con éxito!", {
              duration: 5000,
            });
          }
        },
      });
    }
  };

  const updateEventsLocally = (updatedEvent) => {
    setEvents((currentEvents) =>
      currentEvents.map((evt) =>
        evt.id === updatedEvent.id
          ? {
              ...updatedEvent,
              start: new Date(updatedEvent.hora_inicio),
              end: new Date(updatedEvent.hora_fin),
              title: updatedEvent?.title,
            }
          : evt
      )
    );
  };

  // Cargar datos iniciales
  useEffect(() => {
    dispatch(getCitas());
    dispatch(getPatients());
    setEvents(
      citas?.map((cita) => {
        const start = new Date(cita.hora_inicio);
        const end = new Date(cita.hora_fin);
        return {
          ...cita,
          start,
          end,
          title:
            cita?.patient?.persona?.nombre +
            " " +
            cita?.patient?.persona?.apellidos,
        };
      })
    );
  }, [dispatch, citas?.length]);

  useEffect(() => {
    if (currentView === "day") {
      calculateScrollTime(currentDate);
    }
  }, [currentView, currentDate, events.length]);

  // Función para manejar la selección de un slot
  const handleSelectSlot = (slotInfo) => {
    // No seleccionamos nada en vista mensual
    if (currentView === "month") return;

    setSelectedSlot({
      date: slotInfo.start,
      start: slotInfo.start,
      end: slotInfo.end,
    });
    setOpen(true);
  };

  const handleAddAppointment = () => {
    const now = new Date();

    if (currentView === "day") {
      const dayDate = new Date(currentDate);
      dayDate.setHours(now.getHours(), now.getMinutes());
      const endDate = new Date(dayDate);
      endDate.setMinutes(endDate.getMinutes() + 30);

      setSelectedSlot({
        date: dayDate,
        start: dayDate,
        end: endDate,
      });
    } else {
      const endDate = new Date(now);
      endDate.setMinutes(endDate.getMinutes() + 30);

      setSelectedSlot({
        date: now,
        start: now,
        end: endDate,
      });
    }

    setData({});
    setOpen(true);
  };

  // Manejar cierre del modal
  const handleClose = () => {
    setOpen(false);
    setData({});
  };

  // Manejar clic en un evento
  const handleEventClick = (event) => {
    setData(event);
    setSelectedSlot(null);
    setOpen(true);
  };

  // Manejar cambios de vista
  const handleViewChange = (view) => {
    setCurrentView(view);

    if (view === "day") {
      calculateScrollTime(currentDate);
    }
  };

  // Manejar navegación de fecha
  const handleNavigate = (date) => {
    setCurrentDate(date);

    if (currentView === "day") {
      calculateScrollTime(date);
    }
  };

  const handleShowMore = (events, date) => {
    setCurrentDate(date);
    setCurrentView("day");
  };

  return (
    <Layout>
      {open && (
        <AddAppointmentModal
          datas={data}
          patients={patients?.data}
          isOpen={open}
          currentDay={selectedSlot}
          closeModal={handleClose}
          operation={{
            dispatch: dispatch,
            update: updateEventsLocally,
          }}
        />
      )}
      {/* calender */}
      <button
        onClick={handleAddAppointment}
        className="w-16 animate-bounce h-16 border border-border z-50 bg-subMain text-white rounded-full flex-colo fixed bottom-8 right-12 button-fb"
      >
        <BiPlus className="text-2xl" />
      </button>

      <DndProvider backend={HTML5Backend}>
        <DndCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          min={new Date(0, 0, 0, 7, 0, 0)}
          max={new Date(0, 0, 0, 21, 0, 0)}
          style={{
            height: "82vh",
            marginBottom: 50,
            minHeight: "650px",
          }}
          onSelectEvent={handleEventClick}
          timeslots={3}
          step={5}
          resizeable
          selectable={true}
          messages={{
            month: "Mes",
            week: "Semana",
            day: "Dia",
            showMore: (number) => `+${number} mas . . .`,
          }}
          // Establecer el tiempo de desplazamiento
          scrollToTime={scrollToTime}
          // Controladores para vista y fecha
          view={currentView}
          date={currentDate}
          onView={handleViewChange}
          onNavigate={handleNavigate}
          onShowMore={handleShowMore}
          // custom event style
          eventPropGetter={(event) => {
            const style = {
              backgroundColor:
                event.estado === "en curso"
                  ? "#dc968a"
                  : event.estado === "cerrada"
                  ? "#05B10C"
                  : "#E23F33",
              borderRadius: "10px",
              color: "white",
              border: "1px solid #fff",
              borderColor: "#fff",
              fontSize: "10px",
              padding: "5px 5px",
            };
            return {
              style,
            };
          }}
          // custom date style
          dayPropGetter={(date) => {
            const style = {
              backgroundColor: "white",
            };
            return {
              style,
            };
          }}
          // components
          components={{
            toolbar: (toolbarProps) =>
              CustomToolbar(
                toolbarProps,
                events,
                setSelectedSlot,
                calculateScrollTime
              ),
          }}
          onEventDrop={onEventDrop}
          onSelectSlot={handleSelectSlot}
        />
      </DndProvider>
    </Layout>
  );
}

export default Appointments;
